import { Box, Stack, TextField } from '@mui/material';
import { US_STATES } from 'constants/states';
import QuestionField from '../components/QuestionField';
import ChoiceGroup from '../components/ChoiceGroup';
import ChoiceMultiSelect from '../components/ChoiceMultiSelect';
import SelectInput from '../components/SelectInput';
import ConditionalBranch from '../components/ConditionalBranch';
import StepFooter from '../components/StepFooter';
import {
  ELECTION_GEO_TARGET,
  ELECTION_TYPE,
  FUNDING_STATUS,
  OFFICE_TOP_BALLOT,
  OUTCOME,
  POLITICAL_ORG_TYPE_527,
  PROGRAM_GOAL,
  PROGRAM_YEARS,
} from '../../registryChoices';

// The shared list (also used by Hart) adds the territories and "Other"; the registry only
// accepts the 50 states and DC (backend choices.US_STATES), so anything else fails to save.
const NOT_REGISTRY_STATES = new Set(['AS', 'GU', 'MP', 'PR', 'VI', 'Other']);
const STATE_OPTIONS = US_STATES.filter((s) => !NOT_REGISTRY_STATES.has(s.abbr)).map((s) => ({
  value: s.abbr,
  label: s.name,
}));
const REQUIRED = 'This question is required.';

const Step2Electoral = ({ formData, setField, showErrors, onNext, onBack }) => {
  const err = (missing) => (showErrors && missing ? REQUIRED : '');

  return (
    <>
      <Stack spacing={3.25}>
        <QuestionField
          label="What kind of election is your program targeting?"
          required
          helper="(Please refer to the user guide for the definitions of each)"
          error={err(!formData.election_type)}
        >
          <ChoiceGroup
            layout="columns"
            options={ELECTION_TYPE}
            value={formData.election_type}
            onChange={(v) => setField('election_type', v)}
          />
        </QuestionField>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
          <QuestionField
            label="What date is the election that your program is targeting taking place in?"
            required
            error={err(!formData.election_date)}
          >
            <TextField
              type="date"
              hiddenLabel
              size="small"
              value={formData.election_date || ''}
              onChange={(e) => setField('election_date', e.target.value)}
              error={Boolean(err(!formData.election_date))}
              sx={{ width: 220 }}
            />
          </QuestionField>
          <QuestionField label="During what calendar year(s) will your program be run?">
            <ChoiceMultiSelect
              options={PROGRAM_YEARS}
              value={formData.year}
              onChange={(v) => setField('year', v)}
              placeholder="Add a year..."
            />
          </QuestionField>
        </Box>

        <QuestionField
          label="What is at the top of the ballot in this election?"
          helper="(i.e., what is the highest office for which there is an election)"
        >
          <ChoiceGroup
            multiple
            layout="columns"
            options={OFFICE_TOP_BALLOT}
            value={formData.office_top_ballot}
            onChange={(v) => setField('office_top_ballot', v)}
          />
        </QuestionField>

        <QuestionField
          label="What is the goal of the program?"
          required
          helper="We recognize your program can have multiple goals, but we are currently only pre-registering the Voter Turnout/GOTV goal."
        >
          <SelectInput
            options={PROGRAM_GOAL}
            value={formData.program_goal}
            onChange={(v) => setField('program_goal', v)}
          />
        </QuestionField>

        <QuestionField
          label="What outcomes are you interested in affecting?"
          required
          helper="We recognize that you might be analyzing multiple outcomes, but we are currently only pre-registering Turnout as an outcome."
        >
          <SelectInput options={OUTCOME} value={OUTCOME[0].value} onChange={() => {}} />
        </QuestionField>

        <QuestionField
          label="In what states will you run your program?"
          required
          error={err(formData.states.length === 0)}
        >
          <ChoiceMultiSelect
            options={STATE_OPTIONS}
            value={formData.states}
            onChange={(v) => setField('states', v)}
            placeholder="Add a state..."
            error={Boolean(err(formData.states.length === 0))}
          />
        </QuestionField>

        <QuestionField
          label="What geographic unit is the program targeting?"
          helper="(please select the type of geographic unit that determines where you are running your program, e.g., radio programs might focus on designated market areas, while voter registration programs might focus on particular districts or states)"
        >
          <SelectInput
            options={ELECTION_GEO_TARGET}
            value={formData.election_geo_target}
            onChange={(v) => setField('election_geo_target', v)}
          />
        </QuestionField>
        {formData.election_geo_target === 'other' && (
          <ConditionalBranch>
            <QuestionField
              label="Please write in the geographic unit"
              required
              error={err(!formData.election_geo_target_other.trim())}
            >
              <TextField
                hiddenLabel
                fullWidth
                size="small"
                value={formData.election_geo_target_other}
                onChange={(e) => setField('election_geo_target_other', e.target.value)}
              />
            </QuestionField>
          </ConditionalBranch>
        )}

        <QuestionField
          label="What is the tax/funding status of the program conducting this research?"
          required
          error={err(!formData.funding_status)}
        >
          <SelectInput
            options={FUNDING_STATUS}
            value={formData.funding_status}
            onChange={(v) => setField('funding_status', v)}
            error={Boolean(err(!formData.funding_status))}
          />
        </QuestionField>
        {formData.funding_status === '527' && (
          <ConditionalBranch>
            <QuestionField label="What type of political organization is funding the research?">
              <SelectInput
                options={POLITICAL_ORG_TYPE_527}
                value={formData.political_org_type_527}
                onChange={(v) => setField('political_org_type_527', v)}
              />
            </QuestionField>
          </ConditionalBranch>
        )}
      </Stack>

      <StepFooter onBack={onBack} onNext={onNext} />
    </>
  );
};

export default Step2Electoral;
